import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import api from '../utils/api';
import './PopupManager.css';

const SHOWN_KEY = (id) => `popup_${id}_lastShown`;
const CONVERTED_KEY = (id) => `popup_${id}_lastConverted`;

function readNumber(key) {
  try {
    const v = parseInt(window.localStorage.getItem(key) || '0', 10);
    return Number.isFinite(v) ? v : 0;
  } catch (_) {
    return 0;
  }
}

function writeNumber(key, value) {
  try {
    window.localStorage.setItem(key, String(value));
  } catch (_) {
    /* private mode — ignore */
  }
}

function isInCooldown(popup) {
  const now = Date.now();
  const lastConverted = readNumber(CONVERTED_KEY(popup.id));
  if (lastConverted) {
    const convertedMs = (popup.converted_cooldown_hours || 0) * 60 * 60 * 1000;
    if (convertedMs > 0 && now - lastConverted < convertedMs) return true;
  }
  const lastShown = readNumber(SHOWN_KEY(popup.id));
  if (lastShown) {
    const cooldownMs = (popup.cooldown_minutes || 0) * 60 * 1000;
    if (cooldownMs > 0 && now - lastShown < cooldownMs) return true;
  }
  return false;
}

const PopupManager = () => {
  const [popup, setPopup] = useState(null);
  const [visible, setVisible] = useState(false);
  const [pendingScrollTo, setPendingScrollTo] = useState(null);
  const triggeredRef = useRef(false);
  const location = useLocation();
  const navigate = useNavigate();

  // Fetch the active popup once on mount.
  useEffect(() => {
    let cancelled = false;
    api
      .getActivePopup()
      .then((data) => {
        if (!cancelled && data && data.id) setPopup(data);
      })
      .catch(() => { /* silently no popup */ });
    return () => { cancelled = true; };
  }, []);

  // Wire up the trigger — uniform delay on every device.
  useEffect(() => {
    if (!popup || triggeredRef.current) return;
    if (location.pathname !== '/') return;
    if (isInCooldown(popup)) return;

    const show = () => {
      if (triggeredRef.current) return;
      triggeredRef.current = true;
      writeNumber(SHOWN_KEY(popup.id), Date.now());
      setVisible(true);
    };

    const delayMs = Math.max(popup.delay_seconds || 0, 0) * 1000;
    const timer = setTimeout(show, delayMs);
    return () => clearTimeout(timer);
  }, [popup, location.pathname]);

  // Lock body scroll while open.
  useEffect(() => {
    if (!visible) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [visible]);

  const handleClose = () => setVisible(false);

  const handleCta = () => {
    if (!popup) return;
    writeNumber(CONVERTED_KEY(popup.id), Date.now());
    setVisible(false);

    if (popup.button_type === 'enquiry') {
      const query = popup.title ? `?query=${encodeURIComponent(popup.title)}` : '';
      navigate(`/contact${query}`);
      return;
    }

    // appointment → scroll to homepage form
    if (location.pathname === '/') {
      const el = document.getElementById('appointment-form');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
      setPendingScrollTo('appointment-form');
      navigate('/');
    }
  };

  // After cross-page navigation, wait for ScrollToTop's reset, then scroll.
  useEffect(() => {
    if (!pendingScrollTo || location.pathname !== '/') return;
    const target = pendingScrollTo;
    const t = setTimeout(() => {
      const el = document.getElementById(target);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setPendingScrollTo(null);
    }, 120);
    return () => clearTimeout(t);
  }, [location.pathname, pendingScrollTo]);

  if (!popup || !visible || location.pathname !== '/') return null;

  return (
    <div className="popup-overlay" role="dialog" aria-modal="true" onClick={handleClose}>
      <div className="popup-modal" onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          className="popup-close"
          aria-label="Close popup"
          onClick={handleClose}
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="6" y1="6" x2="18" y2="18" />
            <line x1="18" y1="6" x2="6" y2="18" />
          </svg>
        </button>

        {popup.image && (
          <img src={popup.image} alt="" className="popup-image" />
        )}

        <div className="popup-body">
          <button type="button" className="btn-primary popup-cta" onClick={handleCta}>
            {popup.button_text}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PopupManager;
